import type { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';

import { OperationResult } from 'api/utils/OperationResult';
import { HTTP_STORE } from 'state/http/httpStore';

import { getAccessToken } from './authentication/utils/getAccessToken';

export abstract class ApiService {
  protected readonly url: string;

  protected readonly instance: AxiosInstance;

  constructor(
    url: string,
    protected readonly serviceName: string = 'meta'
  ) {
    this.url = url;

    this.instance = axios.create({
      baseURL: this.url,

      headers: {
        ContentType: 'application/json'
      },

      withCredentials: true
    });

    this.instance.interceptors.request.use((value) => this.requestHandler(value));

    this.instance.interceptors.response.use(
      (value) => this.responseHandler(value),
      (error) => this.errorHandler(error)
    );
  }

  protected isSuccessStatusCode(status: number): boolean {
    return status >= 200 && status <= 299;
  }

  private requestHandler(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
    HTTP_STORE.getState().start(this.serviceName);
    const token = getAccessToken();
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  }

  private responseHandler(response: AxiosResponse): AxiosResponse {
    HTTP_STORE.getState().stop(this.serviceName);
    return response;
  }

  private errorHandler<T extends AxiosError>(error: AxiosError): OperationResult<T> {
    if (!error) {
      return OperationResult.error('An unknown error has occurred');
    }

    const errorMessage = error?.message ?? JSON.stringify(error);

    return OperationResult.error(errorMessage);
  }
}
